import { useState } from "react";

interface JobApplyModalProps {
  jobTitle: string;
  company: string;
  onClose: () => void;
}

export default function JobApplyModal({ jobTitle, company, onClose }: JobApplyModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin: "",
    coverLetter: "",
    resumeName: "",
    experience: "",
    availability: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setForm({ ...form, resumeName: file.name });
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-extrabold text-gray-900">Apply for this Job</h2>
            <p className="text-xs text-gray-400 mt-0.5">{jobTitle} · {company}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-double-line text-green-500 text-4xl" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-2">Application Sent!</h3>
            <p className="text-sm text-gray-500 mb-1">Your application for <strong>{jobTitle}</strong> at <strong>{company}</strong> has been submitted.</p>
            <p className="text-xs text-gray-400 mb-6">The hiring manager will review your resume and reach out via email or the messaging system.</p>
            <button onClick={onClose} className="px-8 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Step Indicator */}
            <div className="flex items-center gap-0 px-6 pt-5 pb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 transition-colors ${step >= s ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>{s}</div>
                  {s < 3 && <div className={`flex-1 h-0.5 mx-1 transition-colors ${step > s ? "bg-orange-500" : "bg-gray-100"}`} />}
                </div>
              ))}
            </div>
            <div className="flex justify-between px-6 mb-4">
              <span className="text-xs font-semibold text-gray-500">Personal Info</span>
              <span className="text-xs font-semibold text-gray-500">Resume</span>
              <span className="text-xs font-semibold text-gray-500">Cover Letter</span>
            </div>

            <div className="px-6 pb-6 space-y-4">
              {step === 1 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label>
                      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="Jane Smith" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone Number *</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="+1 (555) 000-0000" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address *</label>
                    <input name="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="jane@email.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">LinkedIn Profile <span className="font-normal text-gray-400">(optional)</span></label>
                    <input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="linkedin.com/in/yourname" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Years of Experience</label>
                      <select value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer">
                        <option value="">Select...</option>
                        <option>No experience (Entry level)</option>
                        <option>1-2 years</option>
                        <option>3-5 years</option>
                        <option>5-10 years</option>
                        <option>10+ years</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Availability</label>
                      <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer">
                        <option value="">Select...</option>
                        <option>Immediately</option>
                        <option>Within 2 weeks</option>
                        <option>Within 1 month</option>
                        <option>Flexible</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Upload Your Resume *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${form.resumeName ? "border-green-400 bg-green-50" : "border-gray-200 hover:border-orange-300 bg-gray-50"}`}>
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange} />
                      {form.resumeName ? (
                        <>
                          <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mb-2">
                            <i className="ri-file-check-line text-green-500 text-2xl" />
                          </div>
                          <p className="text-sm font-bold text-green-600">{form.resumeName}</p>
                          <p className="text-xs text-gray-400 mt-1">Click to replace</p>
                        </>
                      ) : (
                        <>
                          <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mb-2">
                            <i className="ri-upload-cloud-2-line text-orange-500 text-2xl" />
                          </div>
                          <p className="text-sm font-semibold text-gray-700">Click to upload resume</p>
                          <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX — max 5MB</p>
                        </>
                      )}
                    </label>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                    <p className="text-xs font-bold text-orange-700 mb-1"><i className="ri-lightbulb-line mr-1" />Resume Tips</p>
                    <ul className="text-xs text-orange-600 space-y-1 list-disc list-inside">
                      <li>Keep it to 1-2 pages maximum</li>
                      <li>Include relevant work experience and skills</li>
                      <li>Use clear section headings</li>
                      <li>Proofread for spelling and grammar</li>
                    </ul>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Cover Letter <span className="font-normal text-gray-400">(optional but recommended)</span></label>
                    <textarea
                      value={form.coverLetter}
                      onChange={(e) => setForm({ ...form, coverLetter: e.target.value })}
                      rows={8}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 resize-none"
                      placeholder={`Dear Hiring Manager,\n\nI am excited to apply for the ${jobTitle} position at ${company}. With my background in...\n\nI believe I would be a great fit because...\n\nThank you for your consideration.`}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{form.coverLetter.length}/500</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-xs font-bold text-gray-700 mb-2">Application Summary</p>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Name</span><span className="font-semibold text-gray-800">{form.name || "—"}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Email</span><span className="font-semibold text-gray-800">{form.email || "—"}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Resume</span><span className="font-semibold text-gray-800">{form.resumeName || "Not uploaded"}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Experience</span><span className="font-semibold text-gray-800">{form.experience || "—"}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Available</span><span className="font-semibold text-gray-800">{form.availability || "—"}</span></div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              {step > 1 && (
                <button onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)} className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
                  disabled={step === 1 && (!form.name || !form.email)}
                  className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue <i className="ri-arrow-right-line" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-send-plane-line" /> Submit Application
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
